import moment from 'moment';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ScenarioSegment } from '../../model/scenario-segments.model';
import { Scenario } from "../../model/scenario.model";
import { Helpers } from 'src/app/core/shared/helpers';
import { CategoryValues } from 'src/app/views/product/model/product.model';

export interface UaOverride {
  productBlendedSegmentIds: string[]
  value: number
}

export interface ScenarioMarketingConfigForm {
  mode?: FormControl<string>;
  scenarioId?: FormControl<number>;
  spendLookbackDate?: FormControl<moment.Moment | string>;
  spendLookbackNumberOfDays?: FormControl<number>;
  spendLookbackRelative?: FormControl<boolean>;
  cpiSensitivityCalibrationAutomation?: FormControl<string>;
  cpiSensitivityCalibrationMethod?: FormControl<string>;
  targetPayback?: FormControl<number>;
  roasTargetPercentage?: FormControl<number>;
  paybackMethod?: FormControl<string>;
  blendedSegmentsList?: FormArray<FormGroup<BlendedSegmentFormGroup>>;
  segmentCount?: FormControl<any>
  defaultMarketingFatigue?: FormControl<number>
  defaultCpiSensitivityToSpend?: FormControl<number>
  cpiSensitivityToSpendOverrides?: FormControl<UaOverride[]>
  marketingFatigueOverrides?: FormControl<UaOverride[]>
  buyClv?: FormControl<boolean>
  cpiThreshold?: FormControl<number>;
  ltvThreshold?: FormControl<number>;
  spendThreshold?: FormControl<number>;
}


export interface LookbackFormGroup {
  date: FormControl<moment.Moment | string>;
  numberOfDays: FormControl<number>;
  relative: FormControl<boolean>;
}

export interface ScenarioOrganicsSegmentFormGroup {
  id: FormControl<string | null>;
  manualOrganicFactor: FormControl<number | null>;
  manualTrueOrganics: FormControl<number | null>;
}

export interface ScenarioCpiSegmentFormGroup {
  id: FormControl<string | null>;
  manualMinimumCpi: FormControl<number | null>;
  manualCpiSensitivity: FormControl<number | null>;
}

export interface BlendedSegmentFormGroup {
  segmentId: FormControl<string>;
}

export class BlendedSegment {
  segmentId?: string
  segment?: ScenarioSegment
}

export interface ProductBlendedSegmentNode {
  id: string
  name: string
}

export interface ProductBlendedSegmentFormGroup {
  id: FormControl<string>
  name: FormControl<string>
}

export interface MarketingConfigLookback {
  date: moment.Moment | string;
  numberOfDays: number;
  relative: boolean;
}

interface ModelConfigResponseJson {
  hill_curve: {
    c: number;
    n: number;
    fitted: boolean;
    center_x: number;
    center_y: number;
    lookback: number;
    linear_cpi_at_center_x: number;
  };
  minimum_cpi: number;
  linear_curve: {
    c: number;
    m: number;
    p: number;
    q: number;
    kd: number;
    fitted: boolean;
    target: number;
    max_cpi: number;
    min_cpi: number;
    center_x: number;
    center_y: number;
    lookback: number;
    max_spend: number;
    min_spend: number;
    auto_cpi_sensitivity: number;
  };
  cpi_sensitivity: number;
  marketing_fatigue: number;
  use_low_spend_curve: boolean;
}

export class CpiSensitivitySegmentModelConfig {
  cpiSensitivity: number;
  pValue: number;
  centerY: number;
  centerX: number;

  constructor(data: ModelConfigResponseJson) {
    this.cpiSensitivity = data.cpi_sensitivity;
    this.pValue = data.linear_curve?.p;
    this.centerY = data.linear_curve?.center_y;
    this.centerX = data.linear_curve?.center_x;
  }
}

interface ScenarioCpiSensitivitySegmentResponse extends ScenarioCpiSensitivitySegment {
  autoModelConfig?: CpiSensitivitySegmentModelConfig;
  manualModelConfig?: CpiSensitivitySegmentModelConfig;
}

export class ScenarioCpiSensitivitySegment {
  id?: string;
  scenarioId?: number;
  productBlendedSegmentId?: string;
  autoMinimumCpi?: number;
  autoCpiSensitivity?: number;
  modelConfig?: CpiSensitivitySegmentModelConfig;
  manualMinimumCpi?: number;
  manualCpiSensitivity?: number;
  isManual?: boolean;
  manualUpdatedAt?: moment.Moment | string;
  updatedBy?: string;
  warningType?: UaSegmentWarningType;
  productBlendedSegment?: ProductBlendedSegmentNode;

  static fromJson(json: ScenarioCpiSensitivitySegmentResponse): ScenarioCpiSensitivitySegment {
    const { autoModelConfig, manualModelConfig, ...rest } = json;
    const mergeModelConfig = (autoModelConfig: CpiSensitivitySegmentModelConfig, manualModelConfig: CpiSensitivitySegmentModelConfig) => {
      const parsedAutoModelConfig = typeof autoModelConfig === 'string' ? JSON.parse(autoModelConfig) : autoModelConfig;
      const parsedManualModelConfig = typeof manualModelConfig === 'string' ? JSON.parse(manualModelConfig) : manualModelConfig;
      return {
          ...parsedAutoModelConfig,
          ...parsedManualModelConfig
      }
    }
    return Object.assign(new ScenarioCpiSensitivitySegment(), {
      ...rest,
      autoMinimumCpi: Helpers.toFixedPoint(json.autoMinimumCpi, 2),
      manualMinimumCpi: Helpers.toFixedPoint(json.manualMinimumCpi, 2),
      autoCpiSensitivity: Helpers.toFixedPoint(json.autoCpiSensitivity, 2),
      manualCpiSensitivity: Helpers.toFixedPoint(json.manualCpiSensitivity, 2),
      isManual: json.manualMinimumCpi !== null || json.manualCpiSensitivity !== null,
      manualUpdatedAt: json?.manualUpdatedAt ? moment.utc(json.manualUpdatedAt).toDate() : null,
      modelConfig: new CpiSensitivitySegmentModelConfig(mergeModelConfig(autoModelConfig, manualModelConfig))
    });
  }

  static mapCpiSegmentPreviewData(responseData: CpiPreviewResponse): Highcharts.Options {
    const data = responseData || CpiPreviewResponse.mapNullData();
    return {
      xAxis: {
        title: {
          text: 'scenario.userAcquisition.fields.cpiSensitivityAutomation.axisLabels.x'
        }
      },
      yAxis: {
        title: {
          text: 'scenario.userAcquisition.fields.cpiSensitivityAutomation.axisLabels.cpiY'
        }
      },
      colorAxis: {
        max: data.cpiActualLastYear?.length - 1,
      },
      series: Array.from([
        {
          name: 'scenario.userAcquisition.preview.axis.manualCpiBaseline',
          data: data.manualCpiBaseline?.map(value => ([value.x, value.y])),
          type: 'line'
        },
        {
          name: 'scenario.userAcquisition.preview.axis.autoCpiBaseline',
          data: data.autoCpiBaseline?.map(value => ([value.x, value.y])),
          type: 'line',
        },
        {
          name: 'scenario.userAcquisition.preview.axis.cpiActualLastYear',
          data: data.cpiActualLastYear?.map((value, index) => ({
            x: value.x,
            y: value.y,
            colorValue: index
          })),
          colorKey: 'colorValue',
          type: 'scatter',
          marker: {
            symbol: 'circle',
            radius: 2.5
          },
          className: 'no-stroke'
        }, {
          name: 'scenario.userAcquisition.preview.axis.cpiActualLookback',
          data: data.cpiActualLookback?.map(value => ([value.x, value.y])),
          type: 'scatter',
          marker: {
            symbol: 'triangle',
          },
          colorAxis: false
        }
      ]).filter(Boolean) as Highcharts.Options['series']
    }
  }
  
  static mapDnuPreviewData(responseData: CpiPreviewResponse): Highcharts.Options {
    const data = responseData || CpiPreviewResponse.mapNullData();
    return {
      xAxis: {
        title: {
          text: 'scenario.userAcquisition.fields.cpiSensitivityAutomation.axisLabels.x'
        }
      },
      yAxis: {
        title: {
          text: 'scenario.userAcquisition.fields.cpiSensitivityAutomation.axisLabels.dnuY'
        }
      },
      colorAxis: {
        max: data.dnuActualLastYear.length - 1,
      },
      series: [
        {
          name: 'scenario.userAcquisition.preview.axis.manualDnuBaseline',
          data: data.manualDnuBaseline?.map(value => ([value.x, value.y])),
          type: 'line'
        },
        {
          name: 'scenario.userAcquisition.preview.axis.autoDnuBaseline',
          data: data.autoDnuBaseline?.map(value => ([value.x, value.y])),
          type: 'line',
        },
        {
          name: 'scenario.userAcquisition.preview.axis.dnuActualLastYear',
          data: data.dnuActualLastYear?.map((value, index) => ({
            x: value.x,
            y: value.y,
            colorValue: index
          })),
          colorKey: 'colorValue',
          type: 'scatter',
          marker: {
            symbol: 'circle',
            radius: 2.5
          },
          className: 'no-stroke'
        }, {
          name: 'scenario.userAcquisition.preview.axis.dnuActualLookback',
          data: data.dnuActualLookback?.map(value => ([value.x, value.y])),
          type: 'scatter',
          marker: {
            symbol: 'triangle'
          },
          colorAxis: false
        },
      ].filter(Boolean) as Highcharts.Options['series']
    }
  }

  static getCpiSensitivityBounds(modelConfig: CpiSensitivitySegmentModelConfig): {upperBound: number, lowerBound: number} {
    // Logic below is copied from backend/data science
    const getCValueBasedOnCpiSensitivity = (
      cpiSensitivity: number,
      maximumMValue: number,
      minimumMValue: number,
      centerY: number,
      centerX: number
    ): number => {
      const linearM = minimumMValue + (maximumMValue - minimumMValue) * cpiSensitivity;
      const linearC = centerY - linearM * centerX;
      return linearC;
    };

    const getHillCBoundsFromCpiSensitivity = (
      cpiSensitivity: number,
      pValue: number,
      centerY: number,
      centerX: number
    ): {upperBound: number, lowerBound: number} => {
      // cpiSensitivity is a value between 0 and 1
      const upperBound = getCValueBasedOnCpiSensitivity(
        cpiSensitivity,
        pValue,
        0,  
        centerY,
        centerX
      );
      // in the fitting code, we limit the lower bound to 0.0001 so we can just use the same value here
      const lowerBound = 0.0001;
      return {
        upperBound: Helpers.toFixedPoint(upperBound, 2), 
        lowerBound};
    };
    const { cpiSensitivity, pValue, centerY, centerX } = modelConfig || {};
    return getHillCBoundsFromCpiSensitivity(cpiSensitivity, pValue, centerY, centerX);
  }
}

export class ScenarioOrganicsSegment {
  id?: string;
  scenarioId?: number;
  productBlendedSegmentId?: string;
  autoOrganicFactor?: number;
  autoTrueOrganics?: number;
  manualOrganicFactor?: number;
  manualTrueOrganics?: number;
  isManual?: boolean;
  manualUpdatedAt?: moment.Moment | string;
  updatedBy?: string;
  warningType?: UaSegmentWarningType;
  productBlendedSegment?: ProductBlendedSegmentNode;

  static fromJson(json: ScenarioOrganicsSegment): ScenarioOrganicsSegment {
    return Object.assign(new ScenarioOrganicsSegment(), {
      ...json,
      autoOrganicFactor: Helpers.toFixedPoint(json.autoOrganicFactor, 2),
      manualOrganicFactor: Helpers.toFixedPoint(json.manualOrganicFactor, 2),
      autoTrueOrganics: Helpers.toFixedPoint(json.autoTrueOrganics, 0),
      manualTrueOrganics: Helpers.toFixedPoint(json.manualTrueOrganics, 0),
      isManual: json.manualOrganicFactor !== null || json.manualTrueOrganics !== null,
      manualUpdatedAt: json?.manualUpdatedAt ? moment.utc(json.manualUpdatedAt).toDate() : null
    });
  }

  static mapOrganicsPreviewData(data: OrganicsPreviewResponse): Highcharts.Options {
    if(!data) return null;
    return {
      xAxis: {
        title: {
          text: 'scenario.userAcquisition.fields.organicRelationship.axisLabels.x'
        }
      },
      yAxis: {
        title: {
          text: 'scenario.userAcquisition.fields.organicRelationship.axisLabels.y'
        }
      },
      series: [
        {
          name: 'scenario.userAcquisition.preview.axis.fit',
          id: 'fit',
          data: data.fitLinePoints.map(value => ([value.x, value.y])),
          type: 'line',
          marker: {
            fillColor: '#333333',
          },
          color: '#333333',
        },
        {
          name: 'scenario.userAcquisition.preview.axis.allTime',
          id: 'allTime',
          data: data.allTimeDataPoints?.map((value, index) => ({
            x: value.x,
            y: value.y,
            colorValue: index
          })),
          type: 'scatter',
          marker: {
            symbol: 'circle',
            radius: 2.5,
            fillColor: '#005930',
          },
          color: '#005930',
          className: 'no-stroke'
        }, {
          name: 'scenario.userAcquisition.preview.axis.lookback',
          id: 'lookback',
          data: data.lookbackDataPoints?.map(value => ([value.x, value.y])),
          type: 'scatter',
          marker: {
            symbol: 'circle',
            radius: 2.5,
            fillColor: '#fe6a35',
          },
          color: '#fe6a35',
          className: 'no-stroke'
        }
      ]
    }
  }

  static mapOrganicsSegmentPreviewData(data: OrganicsPreviewResponse): Highcharts.Options {
    if(!data) return null;
    return {
      colorAxis: {
        max: data.allTimeDataPoints?.length - 1,
      },
      series: Array.from([
        {
          name: 'scenario.userAcquisition.preview.axis.autoFit',
          data: data.autoFitLinePoints?.map(value => ([value.x, value.y])),
          type: 'line',
          marker: {
            fillColor: '#333333',
          },
          color: '#333333',
        },
        {
          name: 'scenario.userAcquisition.preview.axis.manualFit',
          data: data.manualFitLinePoints?.map(value => ([value.x, value.y])),
          type: 'line',
          marker: {
            fillColor: 'rgba(0, 226, 114, 1)',
          },
          color: 'rgba(0, 226, 114, 1)',
        },
        {
          name: 'scenario.userAcquisition.preview.axis.allTime',
          data: data.allTimeDataPoints?.map((value, index) => ({
            x: value.x,
            y: value.y,
            colorValue: index
          })),
          colorKey: 'colorValue',
          type: 'scatter',
          marker: {
            symbol: 'circle',
            radius: 2.5,
            fillColor: '#005930'
          },
          color: '#005930',
          className: 'no-stroke'
        }, {
          name: 'scenario.userAcquisition.preview.axis.lookback',
          data: data.lookbackDataPoints?.map(value => ([value.x, value.y])),
          type: 'scatter',
          marker: {
            symbol: 'circle',
            radius: 2.5,
            fillColor: '#fe6a35',
          },
          color: '#fe6a35',
          className: 'no-stroke'
        }
      ]).filter(Boolean) as Highcharts.Options['series']
    }
  }
}

export class ScenarioOrganicsCategoryExclusion {
  id?: string;
  scenarioId?: number;
  productCategoryId?: string;
  productCategory?: CategoryValues;

  constructor(data: ScenarioOrganicsCategoryExclusion) {
    this.id = data.id;
    this.scenarioId = data.scenarioId;
    this.productCategoryId = data.productCategoryId;
    this.productCategory = data.productCategory;
  }

  static fromJsonProductCategory(json: Pick<ScenarioOrganicsCategoryExclusion, 'productCategory'>): ProductBlendedSegmentNode {
    return {
      id: json.productCategory.id,
      name: json.productCategory.value
    };
  }
}

export class ScenarioMarketingConfig {
  id?: number;
  mode?: string;
  scenarioId?: number;
  scenario?: Scenario;
  spendLookbackDate?: moment.Moment | string;
  spendLookbackNumberOfDays?: number;
  spendLookbackRelative?: boolean;
  organicsLookbackDate?: moment.Moment | string;
  organicsLookbackNumberOfDays?: number;
  organicsLookbackRelative?: boolean;
  calibrateOrganicsLastRunTime?: moment.Moment | string;
  cpiSensitivityCalibrationAutomation?: string;
  cpiSensitivityCalibrationMethod?: string;
  cpiSensitivityCalibrationLastRunTime?: moment.Moment | string;
  targetPayback?: number;
  roasTargetPercentage?: number;
  paybackMethod?: string;
  mappedPaybackMethod?: string;
  blendedSegmentsList?: BlendedSegment[];
  blendedSegments?: ScenarioSegment[];
  segmentCount?: any;
  defaultCpiSensitivityToSpend?: number
  defaultMarketingFatigue?: number;
  overrideSegmentsForPaid?: ProductBlendedSegmentNode[]
  overrideSegmentsForPvc?: ProductBlendedSegmentNode[]
  marketingFatigueOverrides?: UaOverride[]
  cpiSensitivityToSpendOverrides?: UaOverride[]
  buyClv?: boolean
  cpiThreshold?: number;
  ltvThreshold?: number;
  spendThreshold?: number;

  static fromJson(json: any): ScenarioMarketingConfig {
    return Object.assign(new ScenarioMarketingConfig(), {
      ...json,
      blendedSegmentsList: json?.blendedSegmentsList?.edges?.map((edge: any) => edge.node) || []
    });
  }

  static isTargetROAS(mode: string): boolean {
    return mode === MarketingMode.TARGET_PAYBACK || mode === MarketingMode.TARGET_DNU_PAYBACK;
  }

  static getPVLookbackDays(spendLookback: MarketingConfigLookback, startDate: moment.Moment | string | Date): number {
    const {relative, date, numberOfDays} = spendLookback;
    const daysFromDate: number = moment(date).diff(moment(startDate), 'days');
    return relative ? numberOfDays : daysFromDate;
  }

  static mapFormValues(form: ScenarioMarketingConfig): ScenarioMarketingConfig {
    return {
      mode: form.mode,
      spendLookbackDate: !!form.spendLookbackDate ? moment(form.spendLookbackDate).format('YYYY-MM-DD') : null,
      spendLookbackNumberOfDays: form.spendLookbackNumberOfDays,
      spendLookbackRelative: form.spendLookbackRelative,
      targetPayback: form.targetPayback,
      roasTargetPercentage: form.roasTargetPercentage,
      paybackMethod: form.paybackMethod,
      defaultCpiSensitivityToSpend: form.defaultCpiSensitivityToSpend,
      defaultMarketingFatigue: form.defaultMarketingFatigue,
      cpiSensitivityToSpendOverrides: form.cpiSensitivityToSpendOverrides,
      marketingFatigueOverrides: form.marketingFatigueOverrides,
      buyClv: form.buyClv,
      cpiThreshold: form.cpiThreshold,
      ltvThreshold: form.ltvThreshold,
      spendThreshold: form.spendThreshold,
      blendedSegmentsList: form.blendedSegmentsList.map((blendedSegment) => ({
        ...blendedSegment,
      }))
    }
  }
}

export enum CalibrationMethod {
  AUTO ='AUTO_ONLY',
  ALL = 'ALL'
}

export enum UaSegmentWarningType {
  BIG_CHANGE = "BIG_CHANGE",
  FIT_LOST = "FIT_LOST",
  FIT_FOUND = "FIT_FOUND",
  INSUFFICIENT_DATA = "INSUFFICIENT_DATA",
  FAILED_TO_FIT = "FAILED_TO_FIT",
  ORGANIC_FACTOR_LIMITED = "ORGANIC_FACTOR_LIMITED"
}

export enum PaybackMethods {
  BLENDED = 'BLENDED',
  PAID = 'PAID'
}

export enum MarketingMode {
  TARGET_NEW_USERS = 'TARGET_NEW_USERS',
  TARGET_SPEND = 'TARGET_SPEND',
  TARGET_PAYBACK = 'TARGET_PAYBACK',
  TARGET_DNU_PAYBACK = 'TARGET_DNU_PAYBACK',
}

export enum CalibrationAutomation {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO',
}

export class PlotPoint {
  x: number
  y: number
}

interface IActual {
  spend?: number;
  cpi?: number;
  dnu?: number;
  clv?: number;
  payback?: number;
}

interface IModel {
  m?: number
  c?: number
}

export class PvcPreviewResponse {
  cpiBaseline: PlotPoint[]
  cpiFatigue: PlotPoint[]
  dnuBaseline: PlotPoint[]
  dnuFatigue: PlotPoint[]
  cpiActualLastYear: PlotPoint[]
  cpiActualLookback: PlotPoint[]
  dnuActualLastYear: PlotPoint[]
  dnuActualLookback: PlotPoint[]
  actualsAvg?: IActual
  pvModel?: IModel
}

export class CpiPreviewResponse {
  autoCpiBaseline?: PlotPoint[]
  autoCpiFatigue?: PlotPoint[]
  autoDnuBaseline?: PlotPoint[]
  autoDnuFatigue?: PlotPoint[]
  autoModel?: IModel
  cpiActualLastYear: PlotPoint[]
  cpiActualLookback: PlotPoint[]
  dnuActualLastYear: PlotPoint[]
  dnuActualLookback: PlotPoint[]
  cpiActualLookbackDate?: moment.Moment | string
  dnuActualLookbackDate?: moment.Moment | string
  manualCpiBaseline?: PlotPoint[]
  manualCpiFatigue?: PlotPoint[]
  manualDnuBaseline?: PlotPoint[]
  manualDnuFatigue?: PlotPoint[]
  manualModel?: IModel

  static mapNullData(): CpiPreviewResponse {
    return {
      autoCpiBaseline: [],
      autoCpiFatigue: [],
      autoDnuBaseline: [],
      autoDnuFatigue: [],
      manualCpiBaseline: [],
      manualCpiFatigue: [],
      manualDnuBaseline: [],
      manualDnuFatigue: [],
      cpiActualLastYear: [],
      cpiActualLookback: [],
      dnuActualLastYear: [],
      dnuActualLookback: []
    }
  }
}

export class OrganicsPreviewResponse {
  allTimeDataPoints: PlotPoint[]
  lookbackDataPoints: PlotPoint[]
  fitLinePoints?: PlotPoint[]
  manualFitLinePoints?: PlotPoint[]
  autoFitLinePoints?: PlotPoint[]
}

export interface UaSegment {
  id: string;
  productBlendedSegment: ProductBlendedSegmentNode;
  isManual: boolean;
  updatedBy: string;
  updatedAt: moment.Moment | string;
  value1: number;
  value2: number | string;
  warningInfo: {
    state: boolean;
    type: string;
  };
}

export class UaSegmentListItem {
  id: string;
  productBlendedSegment: ProductBlendedSegmentNode;
  isManual: boolean;
  updatedBy: string;
  updatedAt: moment.Moment | string;
  value1: number;
  value2: number | string;
  warningInfo: {
    state: boolean;
    type: string;
  };

  constructor(data: UaSegment) {
    this.id = data.id;
    this.productBlendedSegment = data.productBlendedSegment;
    this.isManual = data.isManual;
    this.updatedBy = data.updatedBy;
    this.updatedAt = data.updatedAt;
    this.value1 = data.value1;
    this.value2 = data.value2;
    this.warningInfo = data.warningInfo;
  }
}